import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const tiers = [{
  name: "Small Business",
  price: "$400",
  period: "/year",
  description: "Essential tools for startups and local businesses to establish their presence.",
  features: ["Business Directory Listing", "Access to Networking Events", "Member-Only Newsletter", "Digital Member Badge", "Basic Advocacy Support"],
  cta: "Join Small Business",
  popular: false,
  href: "/JoinUsPage?tier=small-business"
}, {
  name: "Growth",
  price: "$1,200",
  period: "/year",
  description: "Accelerate your expansion with enhanced visibility and strategic connections.",
  features: ["Everything in Small Business", "Priority Directory Placement", "2 Tickets to Annual Gala", "Access to Leadership Programs", "Committee Participation Rights", "Social Media Spotlight"],
  cta: "Join Growth Tier",
  popular: true,
  href: "/JoinUsPage?tier=growth"
}, {
  name: "Corporate",
  price: "$5,000+",
  period: "/year",
  description: "Executive-level influence, policy impact, and maximum brand exposure.",
  features: ["Everything in Growth", "Board of Directors Eligibility", "VIP Access to Policy Makers", "Exclusive Roundtable Invites", "Logo on Homepage Footer", "Custom Sponsorship Opportunities", "Dedicated Account Manager"],
  cta: "Contact for Corporate",
  popular: false,
  href: "/ContactPage"
}];
export default function MembershipOpportunities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="membership-opportunities" ref={ref} className={cn("dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
            Membership Tiers
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the level of engagement that fits your business goals. From
            local startups to major corporations, COHCC provides the platform
            for your success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, index) => <div key={tier.name} data-index={index} className={cn("h-full transition-all duration-300 hover:-translate-y-1", tier.popular ? "md:-mt-4 md:mb-4 z-10" : "")}>
              <Card className={cn("h-full flex flex-col border bg-card/50 backdrop-blur-sm relative overflow-hidden", tier.popular ? "border-primary shadow-lg shadow-primary/10 ring-1 ring-primary/20" : "border-border shadow-md hover:shadow-lg hover:border-primary/30")}>
                {tier.popular && <div className="absolute top-0 right-0">
                    <Badge variant="default" className="rounded-none rounded-bl-lg px-4 py-1 text-xs font-bold uppercase tracking-wider bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  </div>}

                <CardHeader className="pb-8">
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {tier.name}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground mt-2 min-h-[40px]">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">
                      {tier.price}
                    </span>
                    <span className="text-muted-foreground font-medium">
                      {tier.period}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1">
                  <ul className="space-y-4">
                    {tier.features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                        <div className="mt-1 shrink-0 rounded-full bg-primary/10 p-1">
                          <Check className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="text-sm text-muted-foreground leading-tight">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-8">
                  <Button asChild className={cn("w-full font-semibold text-base h-12", tier.popular ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20" : "bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 border border-secondary/20")} variant={tier.popular ? "default" : "secondary"}>
                    <Link to={tier.href}>
                      {tier.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Need a custom solution for your enterprise?
          </p>
          <Button variant="link" asChild className="text-primary text-lg">
            <Link to="/ContactPage">Contact our Membership Team &rarr;</Link>
          </Button>
        </div>
      </div>
    </section>;
}